import { type Container, Filter, GlProgram, Rectangle } from 'pixi.js';
import type { Frame } from '../frame';

const vertex = `
in vec2 aPosition;
out vec2 vTextureCoord;
uniform highp vec4 uInputSize;
uniform vec4 uOutputFrame;
uniform vec4 uOutputTexture;

void main(void) {
  vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;
  position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
  position.y = position.y * (2.0 * uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;
  gl_Position = vec4(position, 0.0, 1.0);
  vTextureCoord = aPosition * (uOutputFrame.zw * uInputSize.zw);
}
`;

// `uShade.x`: how much darker; `uShade.y`: 1 while the drain spreads, 0 while the colors come back.
const fragment = `
in vec2 vTextureCoord;
out vec4 finalColor;
uniform sampler2D uTexture;
uniform highp vec4 uInputSize;
uniform vec4 uBlank;
uniform vec4 uShade;

void main(void) {
  vec4 color = texture(uTexture, vTextureCoord);
  vec3 rgb = color.a > 0.0 ? color.rgb / color.a : vec3(0.0);
  vec2 pixel = vTextureCoord * uInputSize.xy;
  float outside = smoothstep(uBlank.z - 8.0, uBlank.z, distance(pixel, uBlank.xy));
  float amount = uBlank.w * mix(outside, 1.0 - outside, uShade.y);
  float gray = dot(rgb, vec3(0.299, 0.587, 0.114));
  rgb = mix(rgb, vec3(gray), amount) * (1.0 - uShade.x * amount);
  finalColor = vec4(rgb * color.a, color.a);
}
`;

export interface BlankCircle {
  readonly center: { readonly x: number; readonly y: number };
  readonly share: number;
  readonly inside: boolean;
}

export interface BlankFilter {
  set(layer: Container, amount: number, circle: BlankCircle, frame: Frame): number;
  off(layer: Container): void;
  destroy(): void;
}

export function createBlankFilter(world: Container, dark: number): BlankFilter {
  const area = new Rectangle();
  const blank = new Float32Array(4);
  // A vec4 and not two floats: a float changed in place is not sent again.
  const shade = new Float32Array([dark, 0, 0, 0]);
  let attached = false;
  const filter = new Filter({
    glProgram: new GlProgram({ vertex, fragment, name: 'faux-contact-blank' }),
    resolution: 1,
    resources: {
      blankUniforms: {
        uBlank: { value: blank, type: 'vec4<f32>' },
        uShade: { value: shade, type: 'vec4<f32>' },
      },
    },
  });
  return {
    set(layer, amount, circle, frame): number {
      const { center } = circle;
      const scale = world.scale.x;
      const { screenWidth, screenHeight } = frame.camera;
      area.x = -world.x / scale;
      area.y = -world.y / scale;
      area.width = screenWidth / scale;
      area.height = screenHeight / scale;
      const cx = world.x + center.x * scale;
      const cy = world.y + center.y * scale;
      const far = Math.hypot(Math.max(cx, screenWidth - cx), Math.max(cy, screenHeight - cy));
      blank[0] = cx;
      blank[1] = cy;
      blank[2] = circle.share * far;
      shade[1] = circle.inside ? 1 : 0;
      blank[3] = amount;
      if (!attached) {
        layer.filterArea = area;
        layer.filters = [filter];
        attached = true;
      }
      return far / scale;
    },
    off(layer): void {
      if (attached) {
        layer.filters = [];
        attached = false;
      }
    },
    destroy(): void {
      filter.destroy();
    },
  };
}
